import { useEffect, useMemo } from 'react'
import jwtDecode from 'jwt-decode'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import {
  SitemodelManifestStateGrouped,
  ComponentPropsType
} from 'sitemodel/shared/types/sitemodel.type'
import { AuthenticationType } from 'authentication/shared/types'
import useSitemodelState from '../useSitemodelState/useSitemodelState'
import { getApplicationSystemInfo } from 'shared/utils'
import { useTranslation } from 'react-i18n-lite'

interface Credentials {
  user_uuid: string
  organization_uuid: string
  applicationsystem_uuid: string
}

interface DecodedToken {
  credentials: Credentials
}

export interface ApplicationSystem {
  owner: string
}

const useAuthToken = () =>
  useSelector(({ authentication }: AuthenticationType) => authentication?.token)

const useDecodedToken = (token: string | null) =>
  useMemo(() => {
    if (!token) return null
    return jwtDecode<DecodedToken>(token)
  }, [token])

const useAppSystemInfo = () =>
  useMemo<Partial<ApplicationSystem>>(() => {
    const { ownerUuid } = getApplicationSystemInfo()
    return { owner: ownerUuid }
  }, [])

const useSitemodelLocalParams = (stateMap: SitemodelManifestStateGrouped) => {
  const { mergeSitemodelState } = useSitemodelState()
  const [searchParams] = useSearchParams()

  const token = useAuthToken()
  const jwtDecoded = useDecodedToken(token)
  const appSystemInfo = useAppSystemInfo()
  const { language } = useTranslation()

  const credentials = useMemo<Partial<Credentials>>(() => {
    return jwtDecoded?.credentials || {}
  }, [jwtDecoded?.credentials])

  useEffect(() => {
    const newState: ComponentPropsType = {}

    // Querystring mappings
    Object.entries(stateMap.querystring).forEach(([key, cfg]) => {
      newState[key] = searchParams.get(cfg.querystring) ?? undefined

      if (cfg.type === 'boolean') {
        newState[key] = newState[key] === '' || newState[key] === 'true'
      }
    })

    // Credential mappings
    // @deprecated: stateMap.credential is deprecated
    Object.entries(stateMap.credential).forEach(([key, cfg]) => {
      newState[key] = credentials[cfg.credential as keyof Credentials]
    })

    // Logged user credentials
    newState['OrganizationUuid'] = credentials.organization_uuid
    newState['ApplicationsystemUuid'] = credentials.applicationsystem_uuid
    newState['UserUuid'] = credentials.user_uuid

    // Application system mappings
    Object.entries(stateMap.applicationsystem).forEach(([key, cfg]) => {
      newState[key] =
        appSystemInfo[cfg.applicationsystem as keyof ApplicationSystem]
    })

    newState['UserLanguage'] = language

    const currentDate = new Date()

    // CurrentMonthStart
    newState['CurrentMonthStart'] = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    ).toISOString()

    // CurrentMonthEnd
    newState['CurrentMonthEnd'] = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    ).toISOString()

    mergeSitemodelState(newState)
  }, [
    stateMap,
    searchParams,
    credentials,
    appSystemInfo,
    mergeSitemodelState,
    language
  ])
}

export default useSitemodelLocalParams
